import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DisclosureSetOptionsService, DisclosureSetSelectOption } from '../../../../../service/disclosure-set-options.service';

@Component({
  selector: 'auto-advanced-offer-set-configuration',
  templateUrl: './offer-set-configuration.component.html',
  styleUrls: ['./offer-set-configuration.component.scss'],
  standalone: false
})
export class AdvancedAdConfigurationComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly DEFAULT_DISCLOSURE_SET_ID = 1;

  @Input() data: any | null = null;
  @Input() showDisclosuresTab = false;
  @Output() dataEmitter = new EventEmitter<UntypedFormGroup>();
  form: UntypedFormGroup;
  disclosureSetOptions: DisclosureSetSelectOption[] = [];
  disclosureSetTooltip = '';
  private offerSetAdvancedSettings: any;

  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private disclosureSetOptionsService: DisclosureSetOptionsService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      accountOfferSetRefresh: [false],
      customTitleDisplayOverride: [false],
      newOfferSetCustomTitleOverrideText: [''],
      usedOfferSetCustomTitleOverrideText: [''],
      cpoOfferSetCustomTitleOverrideText: [''],
      minimizeDisclaimer: [false],
      newMainCtaText: [''],
      usedMainCtaText: [''],
      disclosureSet: [0],
      enhancedDisclosure: [false],
      ncsUseLatestSpecialsUx: [false],
      sharedOfferSetsEnabled: [false],
      enhancedDisclosureEnabled: [false]
    });

    this.form.get('enhancedDisclosure')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        this.offerSetAdvancedSettings = { ...(this.offerSetAdvancedSettings || {}), enhancedDisclosure: enabled };
        this.disclosureSetOptions = this.disclosureSetOptionsService.buildDisclosureSetOptions(this.offerSetAdvancedSettings);

        const currentValue = this.form.get('disclosureSet')?.value;
        const exists = this.disclosureSetOptions.some(option => option.value === currentValue);

        if (!exists) {
          this.form.patchValue(
            { disclosureSet: AdvancedAdConfigurationComponent.DEFAULT_DISCLOSURE_SET_ID },
            { emitEvent: false }
          );
        }

        this.updateDisclosureSetTooltip();
      });

    this.dataEmitter.emit(this.form);
    this.patchFromInput(this.data);

    this.form.get('disclosureSet')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateDisclosureSetTooltip();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.patchFromInput(changes['data'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private patchFromInput(offerSetAdvancedSettings: any): void {
    if (offerSetAdvancedSettings && this.form) {
      this.offerSetAdvancedSettings = offerSetAdvancedSettings;

      // Build options
      this.disclosureSetOptions = this.disclosureSetOptionsService.buildDisclosureSetOptions(offerSetAdvancedSettings);

      // Selected disclosure set
      let selectedDisclosureSet = 0;
      if (Number.isFinite(offerSetAdvancedSettings.disclosureSet)) {
        selectedDisclosureSet = Number(offerSetAdvancedSettings.disclosureSet);
      }

      this.form.patchValue(
        {
          accountOfferSetRefresh: !!offerSetAdvancedSettings.accountOfferSetRefresh,
          customTitleDisplayOverride: !!offerSetAdvancedSettings.customTitleDisplayOverride,
          newOfferSetCustomTitleOverrideText: offerSetAdvancedSettings.newOfferSetCustomTitleOverrideText || '',
          usedOfferSetCustomTitleOverrideText: offerSetAdvancedSettings.usedOfferSetCustomTitleOverrideText || '',
          cpoOfferSetCustomTitleOverrideText: offerSetAdvancedSettings.cpoOfferSetCustomTitleOverrideText || '',
          minimizeDisclaimer: !!offerSetAdvancedSettings.minimizeDisclaimer,
          newMainCtaText: offerSetAdvancedSettings.newMainCtaText || '',
          usedMainCtaText: offerSetAdvancedSettings.usedMainCtaText || '',
          disclosureSet: selectedDisclosureSet,
          enhancedDisclosure: !!offerSetAdvancedSettings.enhancedDisclosure,
          sharedOfferSetsEnabled: !!offerSetAdvancedSettings.sharedOfferSetsEnabled,
        },
        { emitEvent: false }
      );

      this.updateDisclosureSetTooltip();

      this.form.markAsPristine();
    }
  }

  private updateDisclosureSetTooltip(): void {
    this.disclosureSetOptionsService.updateDisclosureSetTooltip(
      this.form,
      this.disclosureSetOptions,
      (tooltip) => { this.disclosureSetTooltip = tooltip; }
    );
  }

}
